<template>
  <div class="history-card">
    <div class="history-header">
      <h3>{{ t('Histórico') }}</h3>
      <SelectButton
        v-if="steps.length > 0"
        v-model="viewMode"
        :options="viewModeOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
        data-testid="production-history-view-toggle">
        <template #option="slotProps">
          <i :class="slotProps.option.icon"></i>
          <span class="view-option-label">{{ slotProps.option.label }}</span>
        </template>
      </SelectButton>
    </div>

    <div v-if="props.logs.length === 0 && steps.length === 0" class="history-empty">
      <i class="pi pi-clock"></i>
      <p>{{ t('Nenhum registro de produção') }}</p>
    </div>

    <Timeline
      v-else-if="effectiveViewMode === 'timeline'"
      :value="sortedLogs"
      align="left"
      class="production-timeline">
      <template #marker="slotProps">
        <span class="timeline-marker" :class="badgeClass(slotProps.item)"></span>
      </template>
      <template #content="slotProps">
        <div class="timeline-entry">
          <div class="timeline-topline">
            <span class="timeline-time">{{ formatTimestamp(slotProps.item.so_production_log_scanned_at || slotProps.item.created_at) }}</span>
            <span class="timeline-badge" :class="badgeClass(slotProps.item)">{{ actionLabel(slotProps.item) }}</span>
          </div>
          <strong v-if="slotProps.item.scanned_step_name || slotProps.item.so_production_log_expected_step_name" class="timeline-step">
            {{ slotProps.item.scanned_step_name || slotProps.item.so_production_log_expected_step_name }}
          </strong>
          <span v-if="slotProps.item.scanned_by_name" class="timeline-user">{{ slotProps.item.scanned_by_name }}</span>
          <p v-if="slotProps.item.so_production_log_notes" class="timeline-notes">{{ slotProps.item.so_production_log_notes }}</p>
        </div>
      </template>
    </Timeline>

    <div v-else>
      <div v-for="step in sortedSteps" :key="step.so_work_order_step_id" class="step-group">
        <div class="step-group-header">
          <div class="step-group-title">
            <span class="step-group-dot" :class="stepStatusClass(step.so_work_order_step_status)"></span>
            <strong>{{ step.so_work_order_step_name }}</strong>
          </div>
          <span class="step-status-badge" :class="stepStatusClass(step.so_work_order_step_status)">
            {{ stepStatusLabel(step.so_work_order_step_status) }}
          </span>
        </div>

        <div v-if="stepLogs(step).length > 0" class="step-logs">
          <div v-for="(logItem, index) in stepLogs(step)" :key="timelineKey(logItem, index)" class="step-log-item">
            <span class="step-log-marker" :class="badgeClass(logItem)"></span>
            <div class="step-log-content">
              <div class="step-log-topline">
                <span class="timeline-time">{{ formatTimestamp(logItem.so_production_log_scanned_at || logItem.created_at) }}</span>
                <span class="timeline-badge" :class="badgeClass(logItem)">{{ actionLabel(logItem) }}</span>
              </div>
              <span v-if="logItem.scanned_by_name" class="timeline-user">{{ logItem.scanned_by_name }}</span>
              <p v-if="logItem.so_production_log_notes" class="timeline-notes">{{ logItem.so_production_log_notes }}</p>
            </div>
          </div>
        </div>
        <div v-else class="step-empty">
          <i class="pi pi-clock"></i>
          <span>{{ t('Sem registros para esta etapa') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SelectButton from '../../../ui/SelectButton.vue';
import type { Orders_WorkOrderStepRow } from '../../../contracts/Orders_WorkOrderStepRow';

type Id = number | string;
type ViewMode = 'timeline' | 'steps';

interface ProductionLogItem {
  so_production_log_id?: Id;
  so_production_log_action?: string;
  so_production_log_scanned_at?: string;
  so_production_log_expected_step_name?: string;
  so_production_log_notes?: string;
  so_production_log_production_step_id?: number;
  scanned_step_name?: string;
  scanned_by_name?: string;
  action?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface Props {
  logs?: ProductionLogItem[];
  steps?: Orders_WorkOrderStepRow[];
}

const props = withDefaults(defineProps<Props>(), {
  logs: () => [],
  steps: () => [],
});

const { t } = useI18n();

const viewMode = ref<ViewMode>('steps');

const viewModeOptions = computed(() => [
  { label: t('Linha do tempo'), value: 'timeline', icon: 'pi pi-clock' },
  { label: t('Etapas'), value: 'steps', icon: 'pi pi-list' },
]);

const effectiveViewMode = computed<ViewMode>(() =>
  props.steps.length === 0 ? 'timeline' : viewMode.value
);

const sortedLogs = computed(() => {
  const toMs = (item: ProductionLogItem): number => {
    const raw = item.so_production_log_scanned_at || item.created_at;
    if (!raw) return 0;
    const ms = new Date(raw).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  };
  return [...props.logs].sort((a, b) => toMs(a) - toMs(b));
});

const sortedSteps = computed(() =>
  [...props.steps].sort((a, b) => a.so_work_order_step_order - b.so_work_order_step_order)
);

const stepLogs = (step: Orders_WorkOrderStepRow): ProductionLogItem[] =>
  sortedLogs.value.filter(
    (log) => log.so_production_log_production_step_id === step.so_work_order_step_production_step_id
  );

const stepStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    pending: t('Pendente'),
    in_progress: t('Em andamento'),
    completed: t('Concluído'),
    skipped: t('Pulado'),
    reprocess_pending: t('Reprocesso pendente'),
  };
  return map[status] || status;
};

const stepStatusClass = (status: string): string => {
  const map: Record<string, string> = {
    pending: 'status-pending',
    in_progress: 'status-in-progress',
    completed: 'status-completed',
    skipped: 'status-skipped',
    reprocess_pending: 'status-warning',
  };
  return map[status] || 'status-pending';
};

const badgeClass = (logItem: ProductionLogItem): string => {
  const action = logItem?.so_production_log_action || logItem?.action || '';
  if (['start', 'complete', 'completed', 'in_progress', 'reprocess_complete'].includes(action)) {
    return 'badge-success';
  }
  if (['out_of_order', 'out_of_order_start', 'out_of_order_warning', 'reprocess_pending', 'reprocess_start', 'aborted_by_reprocess'].includes(action)) {
    return 'badge-warning';
  }
  if (['blocked', 'blocked_no_laudo', 'blocked_delivered', 'error'].includes(action)) {
    return 'badge-danger';
  }
  return 'badge-info';
};

const actionLabel = (logItem: ProductionLogItem): string => {
  const action = logItem?.so_production_log_action || logItem?.action || '';
  const labels: Record<string, string> = {
    start: t('Início'),
    complete: t('Conclusão'),
    completed: t('Concluído'),
    out_of_order: t('Fora de ordem'),
    out_of_order_start: t('Fora de ordem'),
    out_of_order_warning: t('Atenção'),
    out_of_order_complete: t('Fora de ordem'),
    blocked: t('Bloqueado'),
    blocked_no_laudo: t('Bloqueado'),
    blocked_delivered: t('Bloqueado - item entregue'),
    error: t('Erro'),
    info: t('Info'),
    info_all_done: t('Tudo concluído'),
    reprocess_pending: t('Reprocesso solicitado'),
    reprocess_start: t('Reprocesso iniciado'),
    reprocess_complete: t('Reprocesso concluído'),
    aborted_by_reprocess: t('Abortado por reprocesso'),
  };
  return labels[action] || t('Info');
};

const formatTimestamp = (timestamp?: string): string => {
  if (!timestamp) {
    return '-';
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const timelineKey = (logItem: ProductionLogItem, index: number): string =>
  `${logItem.so_production_log_id || 'log'}-${index}`;
</script>

<style scoped>
.history-card {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  padding: 1rem;
  min-height: 100%;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.history-header h3 {
  margin: 0;
}

.view-option-label {
  margin-left: 0.4rem;
}

.history-empty {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
}

.production-timeline :deep(.p-timeline-event-opposite) {
  display: none;
}

.timeline-marker {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  display: inline-block;
}

.timeline-entry {
  padding-bottom: 1rem;
}

.timeline-topline {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
  flex-wrap: wrap;
}

.timeline-time {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.9rem;
}

.timeline-badge {
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}

.timeline-step {
  display: block;
  margin-bottom: 0.3rem;
}

.timeline-user {
  display: block;
  font-size: 0.8rem;
  color: var(--p-text-muted-color, #6b7280);
  margin-bottom: 0.2rem;
}

.timeline-notes {
  margin: 0;
  color: var(--p-text-muted-color, #6b7280);
}

/* Step-segmented view */
.step-group {
  margin-bottom: 1rem;
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
  overflow: hidden;
}

.step-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.65rem 0.85rem;
  background: var(--p-surface-50, #f8f9fa);
  border-bottom: 1px solid var(--p-surface-200, #e5e7eb);
}

.step-group-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.step-group-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
}

.step-status-badge {
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.step-logs {
  padding: 0.5rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.step-log-item {
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: 0.75rem;
  align-items: start;
}

.step-log-marker {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  margin-top: 0.35rem;
}

.step-log-content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.step-log-topline {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.step-empty {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.85rem;
  color: var(--p-text-muted-color, #9ca3af);
  font-size: 0.85rem;
  font-style: italic;
}

.step-empty i {
  font-size: 0.9rem;
}

/* Step statuses */
.status-pending {
  background: var(--p-surface-200, #e5e7eb);
  color: var(--p-text-muted-color, #6b7280);
}

.status-in-progress {
  background: rgba(59, 130, 246, 0.16);
  color: #1d4ed8;
}

.status-completed {
  background: rgba(34, 197, 94, 0.15);
  color: #166534;
}

.status-skipped {
  background: rgba(239, 68, 68, 0.16);
  color: #991b1b;
}

.status-warning {
  background: rgba(245, 158, 11, 0.18);
  color: #92400e;
}

/* Log badges */
.badge-success {
  background: rgba(34, 197, 94, 0.15);
  color: #166534;
}

.badge-warning {
  background: rgba(245, 158, 11, 0.18);
  color: #92400e;
}

.badge-danger {
  background: rgba(239, 68, 68, 0.16);
  color: #991b1b;
}

.badge-info {
  background: rgba(59, 130, 246, 0.16);
  color: #1d4ed8;
}
</style>
